/**
 * Assignment grouping — shared by the assignments landing and the configure
 * wizard.
 *
 * The backend stores one CourseAssignment row *per learner membership*, so a
 * single authored "audience" (one /assign call) materialises as N rows that
 * share the same title / dates / channel / send-state. We group those rows back
 * into one configuration so the UI shows one card per audience, and so the
 * wizard can reopen a draft by aggregating its rows' learners.
 */
import type { Assignment, AssignChannel } from '../../types/assignment';

/** One authored configuration: every per-learner row created by a single /assign. */
export interface AssignmentGroup {
  key: string;
  title: string | null;
  start_date: string | null;
  end_date: string | null;
  publish_date: string | null;
  channel: AssignChannel | null;
  channels: AssignChannel[] | null;
  invite_message: string | null;
  is_notified: boolean;
  rows: Assignment[];
}

export function groupKey(a: Assignment): string {
  return [
    a.title ?? '',
    a.start_date ?? '',
    a.end_date ?? '',
    a.publish_date ?? '',
    a.channel ?? '',
    a.is_notified ? '1' : '0',
  ].join('|');
}

/** Collapse per-learner rows into one entry per authored configuration. */
export function groupAssignments(assignments: Assignment[]): AssignmentGroup[] {
  const map = new Map<string, AssignmentGroup>();
  for (const a of assignments) {
    const key = groupKey(a);
    const existing = map.get(key);
    if (existing) {
      existing.rows.push(a);
    } else {
      map.set(key, {
        key,
        title: a.title,
        start_date: a.start_date,
        end_date: a.end_date,
        publish_date: a.publish_date,
        channel: a.channel,
        channels: a.channels ?? null,
        invite_message: a.invite_message,
        is_notified: a.is_notified,
        rows: [a],
      });
    }
  }
  return Array.from(map.values());
}

/** Find the group that contains a given assignment row id. */
export function findGroupByRowId(
  groups: AssignmentGroup[],
  rowId: string
): AssignmentGroup | undefined {
  return groups.find((g) => g.rows.some((r) => r.id === rowId));
}
